import React, { useEffect, useState } from 'react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import {
  fetchDeliveryBoys,
  createDeliveryBoy,
  updateDeliveryBoy,
  updateDeliveryBoyStatus,
  deleteDeliveryBoy,
} from '../services/deliveryBoyService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Trash,
} from 'lucide-react';
import { uploadFile } from '../services/uploadService';

export default function DeliveryExecutives() {
  const [executives, setExecutives] = useState([]);
  const [search, setSearch] = useState('');
  const initialForm = {
    name: '',
    phone: '',
    email: '',
    status: '1',
    type: '1',
    outsourceProvider: '',
    outsourceId: '',
    employmentType: '1',
    bankAccountNumber: '',
    bankIfscCode: '',
    bankName: '',
    bankBranch: '',
    bankAccountHolderName: '',
    loginEmail: '',
    loginPassword: '',
    attachments: [],
  };
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [statusFilter, setStatusFilter] = useState('');

  const loadExecutives = async () => {
    try {
      const res = await fetchDeliveryBoys({
        search,
        page,
        limit,
        sortBy,
        sortOrder,
        status: statusFilter,
      });
      const records = res.data?.data?.records || res.data?.data || [];
      const meta = res.data?.data?.meta || {};
      setExecutives(records);
      setTotalPages(meta.pages || 1);
      setTotalRecords(meta.total || records.length);
    } catch (err) {
      toast.error('Failed to load delivery executives');
    }
  };

  useEffect(() => {
    loadExecutives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sortBy, sortOrder, statusFilter]);

  useEffect(() => {
    if (search === '') {
      setPage(1);
      loadExecutives();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const exportToXls = () => {
    const headers = ['Name', 'Phone', 'Email'];
    const rows = executives.map((e) => [e.name, e.phone || '', e.email || '']);
    const table = [headers, ...rows]
      .map((r) => r.map((v) => `"${v ?? ''}"`).join('\t'))
      .join('\n');
    const blob = new Blob(['\ufeff', table], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'delivery-executives.xls';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.name) newErrors.name = 'Name is required';
    if (!form.phone) newErrors.phone = 'Phone is required';
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email,
        status: Number(form.status),
        type: Number(form.type),
        employmentType: Number(form.employmentType),
        bankAccountNumber: form.bankAccountNumber,
        bankIfscCode: form.bankIfscCode,
        bankName: form.bankName,
        bankBranch: form.bankBranch,
        bankAccountHolderName: form.bankAccountHolderName,
      };
      if (form.type === '2') {
        payload.outsourceProvider = form.outsourceProvider;
        payload.outsourceId = form.outsourceId;
      }
      const login = {};
      if (form.loginEmail) login.email = form.loginEmail;
      if (form.loginPassword) login.password = form.loginPassword;
      if (Object.keys(login).length) payload.login = login;
      if (form.attachments.length) payload.attachments = form.attachments;
      if (editingId) {
        await updateDeliveryBoy(editingId, payload);
        toast.success('Delivery executive updated');
      } else {
        await createDeliveryBoy(payload);
        toast.success('Delivery executive created');
      }
      closeForm();
      loadExecutives();
    } catch (err) {
      toast.error('Failed to save delivery executive');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setForm(initialForm);
    setErrors({});
    setOpen(true);
  };

  const handleEdit = (exec) => {
    setEditingId(exec.id);
    setForm({
      name: exec.name || '',
      phone: exec.phone || '',
      email: exec.email || '',
      status: String(exec.status ?? 1),
      type: String(exec.type ?? 1),
      outsourceProvider: exec.outsourceProvider || '',
      outsourceId: exec.outsourceId || '',
      employmentType: String(exec.employmentType ?? 1),
      bankAccountNumber: exec.bankAccountNumber || '',
      bankIfscCode: exec.bankIfscCode || '',
      bankName: exec.bankName || '',
      bankBranch: exec.bankBranch || '',
      bankAccountHolderName: exec.bankAccountHolderName || '',
      loginEmail: exec.login?.email || '',
      loginPassword: '',
      attachments: [],
    });
    setErrors({});
    setOpen(true);
  };

  const closeForm = () => {
    setEditingId(null);
    setForm(initialForm);
    setErrors({});
    setOpen(false);
  };

  const addAttachment = () => {
    setForm({
      ...form,
      attachments: [...form.attachments, { attachmentType: '', fileUrl: '' }],
    });
  };

  const updateAttachment = (index, field, value) => {
    const newAttachments = [...form.attachments];
    newAttachments[index] = { ...newAttachments[index], [field]: value };
    setForm({ ...form, attachments: newAttachments });
  };

  const handleAttachmentFile = async (index, file) => {
    if (!file) return;
    try {
      const res = await uploadFile(file);
      const url = res.data?.data?.fileUrl || '';
      updateAttachment(index, 'fileUrl', url);
    } catch (err) {
      toast.error('Failed to upload file');
    }
  };

  const removeAttachment = (index) => {
    const newAttachments = form.attachments.filter((_, i) => i !== index);
    setForm({ ...form, attachments: newAttachments });
  };

  const viewAttachments = (attachments) => {
    if (!attachments || !attachments.length) {
      toast.info('No attachments');
      return;
    }
    const html = attachments
      .map((a) => `<a href="${a.fileUrl}" target="_blank" class="text-indigo-600">${a.attachmentType}</a>`)
      .join('<br/>');
    Swal.fire({ title: 'Attachments', html });
  };

  const toggleStatus = async (exec) => {
    const newStatus = exec.status === 1 || exec.status === '1' ? 2 : 1;
    const action = newStatus === 1 ? 'activate' : 'deactivate';
    const result = await Swal.fire({
      title: `Are you sure you want to ${action} this delivery executive?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-green-600 text-white px-4 py-2 rounded mr-2 hover:bg-green-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await updateDeliveryBoyStatus(exec.id, newStatus);
      toast.success('Status updated');
      loadExecutives();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this delivery executive?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-red-600 text-white px-4 py-2 rounded mr-2 hover:bg-red-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await deleteDeliveryBoy(id);
      toast.success('Delivery executive deleted');
      loadExecutives();
    } catch (err) {
      toast.error('Failed to delete delivery executive');
    }
  };

  return (
    <LayoutWithSidebar>
      <h1 className="text-2xl font-bold mb-4">Delivery Executives</h1>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-2 md:space-y-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadExecutives();
          }}
          className="flex items-center relative"
        >
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="border px-3 py-2 pl-8 pr-8 rounded w-full md:w-64 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                loadExecutives();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <X size={16} />
            </button>
          )}
        </form>

        <div className="flex space-x-2">
          <button
            onClick={exportToXls}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            Export XLS
          </button>
          <button
            onClick={handleAdd}
            className="bg-indigo-600 text-white px-4 py-2 rounded"
          >
            Add Delivery Executive
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left">
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Name</span>
                  {sortBy === 'name' ? (
                    sortOrder === 'ASC' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Phone</th>
              <th className="py-2 px-3">Email</th>
              <th className="py-2 px-3">Attachments</th>
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center space-x-1">
                  <span>Created At</span>
                  {sortBy === 'createdAt' ? (
                    sortOrder === 'ASC' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {executives.map((exec) => (
              <tr key={exec.id} className="border-t dark:border-gray-700">
                <td className="py-2 px-3">{exec.name}</td>
                <td className="py-2 px-3">{exec.phone}</td>
                <td className="py-2 px-3">{exec.email}</td>
                <td className="py-2 px-3">
                  {exec.attachments && exec.attachments.length ? (
                    <button
                      className="text-indigo-600 underline"
                      onClick={() => viewAttachments(exec.attachments)}
                    >
                      View ({exec.attachments.length})
                    </button>
                  ) : (
                    <button
                      className="text-indigo-600 underline"
                      onClick={() => handleEdit(exec)}
                    >
                      Add
                    </button>
                  )}
                </td>
                <td className="py-2 px-3">{new Date(exec.createdAt).toLocaleDateString()}</td>
                <td className="py-2 px-3">
                  <button
                    className={`px-2 py-1 rounded text-white ${
                      exec.status === 1 || exec.status === '1'
                        ? 'bg-green-500'
                        : 'bg-red-500'
                    }`}
                    onDoubleClick={() => toggleStatus(exec)}
                    title={`Double click to ${
                      exec.status === 1 || exec.status === '1'
                        ? 'inactive'
                        : 'active'
                    }`}
                  >
                    {exec.status === 1 || exec.status === '1'
                      ? 'Active'
                      : 'Inactive'}
                  </button>
                </td>
                <td className="py-2 px-3 space-x-2 flex items-center">
                  <button className="text-indigo-600" onClick={() => handleEdit(exec)}>
                    <Edit size={16} />
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => handleDelete(exec.id)}
                  >
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-4 space-y-2 md:space-y-0">
        <div className="text-sm text-gray-600">
          Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalRecords)} of {totalRecords} entries
        </div>
        <div className="flex justify-center space-x-2">
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          <span className="px-2 py-1">
            Page {page} of {totalPages}
          </span>
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 flex justify-end">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full sm:w-96 h-full p-4 overflow-y-auto">
            {loading && (
              <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10">
                <div className="h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">{editingId ? 'Edit Delivery Executive' : 'Add Delivery Executive'}</h2>
              <button onClick={closeForm} className="text-gray-600">×</button>
            </div>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
              <div>
                <input
                  name="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Name"
                  className={`border px-3 py-2 rounded w-full ${errors.name ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>
              <div>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone"
                  className={`border px-3 py-2 rounded w-full ${errors.phone ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
              </div>
              <div>
                <input
                  name="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email"
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <select
                  name="type"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="1">In-house</option>
                  <option value="2">Outsourced</option>
                </select>
              </div>
              {form.type === '2' && (
                <>
                  <div>
                    <select
                      name="outsourceProvider"
                      value={form.outsourceProvider}
                      onChange={(e) => setForm({ ...form, outsourceProvider: e.target.value })}
                      className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">Select Provider</option>
                      <option value="Zomato">Zomato</option>
                      <option value="Swiggy">Swiggy</option>
                    </select>
                  </div>
                  <div>
                    <input
                      name="outsourceId"
                      value={form.outsourceId}
                      onChange={(e) => setForm({ ...form, outsourceId: e.target.value })}
                      placeholder="Outsource ID"
                      className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </>
              )}
              <div>
                <select
                  name="employmentType"
                  value={form.employmentType}
                  onChange={(e) => setForm({ ...form, employmentType: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="1">Full Time</option>
                  <option value="2">Part Time</option>
                </select>
              </div>
              <div>
                <input
                  name="bankAccountNumber"
                  value={form.bankAccountNumber}
                  onChange={(e) => setForm({ ...form, bankAccountNumber: e.target.value })}
                  placeholder="Bank Account Number"
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <input
                  name="bankIfscCode"
                  value={form.bankIfscCode}
                  onChange={(e) => setForm({ ...form, bankIfscCode: e.target.value })}
                  placeholder="IFSC Code"
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <input
                  name="bankName"
                  value={form.bankName}
                  onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                  placeholder="Bank Name"
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <input
                  name="bankBranch"
                  value={form.bankBranch}
                  onChange={(e) => setForm({ ...form, bankBranch: e.target.value })}
                  placeholder="Bank Branch"
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <input
                  name="bankAccountHolderName"
                  value={form.bankAccountHolderName}
                  onChange={(e) => setForm({ ...form, bankAccountHolderName: e.target.value })}
                  placeholder="Account Holder Name"
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <input
                  name="loginEmail"
                  value={form.loginEmail}
                  onChange={(e) => setForm({ ...form, loginEmail: e.target.value })}
                  placeholder="Login Email"
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <input
                  type="password"
                  name="loginPassword"
                  value={form.loginPassword}
                  onChange={(e) => setForm({ ...form, loginPassword: e.target.value })}
                  placeholder={editingId && form.loginEmail ? 'New Password' : 'Password'}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="font-medium">Attachments</label>
                {form.attachments.map((att, idx) => (
                  <div key={idx} className="flex items-center space-x-2 mt-2">
                    <select
                      value={att.attachmentType}
                      onChange={(e) => updateAttachment(idx, 'attachmentType', e.target.value)}
                      className="border px-3 py-2 rounded w-1/3 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">Select Type</option>
                      <option value="certificate">Certificate</option>
                      <option value="id card">ID Card</option>
                      <option value="licence">Licence</option>
                    </select>
                    <input
                      type="file"
                      onChange={(e) => handleAttachmentFile(idx, e.target.files[0])}
                      className="border px-3 py-2 rounded w-1/3 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    {att.fileUrl && (
                      <a
                        href={att.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600"
                      >
                        View
                      </a>
                    )}
                    <button type="button" onClick={() => removeAttachment(idx)} className="text-red-600">
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addAttachment}
                  className="mt-2 text-indigo-600 underline"
                >
                  Add Attachment
                </button>
              </div>
              <div>
                <select
                  name="status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="1">Active</option>
                  <option value="2">Inactive</option>
                </select>
              </div>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded"
                disabled={loading}
              >
                {editingId ? 'Update' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}
